import type { CorrelationFilter } from '../analytics-correlation-filter/analytics-correlation-filter.component'
import type { CorrelationSetting } from '../analytics-correlation-setting/analytics-correlation-setting.component'
import type { IAccountData, AnalyticsCorrelationService } from '../analytics-correlation.service'

import { app } from '../../../app.module'

/**
 * 相関グラフ
 * @class AnalyticsCorrelationGraphCtrl
 */
class AnalyticsCorrelationGraphCtrl {
  private snsType: string
  private graphConfig: any
  private graphSetting: CorrelationSetting
  private filter: CorrelationFilter
  private accounts: IAccountData[]
  // 対象データ
  private objectDatas: IAccountData[]
  private graphData: {
    x: number
    y: number
    z: number
    name: string
    fullName: string
  }[]

  constructor(
    private $rootScope,
    private $filter,
    private AnalyticsCorrelationService: AnalyticsCorrelationService,
    private $mdDialog
  ) {}

  /**
   * 更新イベント
   */
  public $onChanges() {
    if (this.accounts.length === 0) {
      return
    }
    // グラフ設定の初期化
    this.setGraphConfig()
  }

  /**
   * グラフ設定の初期化
   * @private
   */
  private setGraphConfig() {
    // グラフデータをセット
    this.setGraphData()

    let xAxisPercent = ''
    if (
      this.graphSetting.xAxis.value === 'engagementRate' ||
      this.graphSetting.xAxis.value === 'followersUpdownRate'
    ) {
      xAxisPercent = '%'
    }
    let yAxisPercent = ''
    if (
      this.graphSetting.yAxis.value === 'engagementRate' ||
      this.graphSetting.yAxis.value === 'followersUpdownRate'
    ) {
      yAxisPercent = '%'
    }
    let zAxisPercent = ''
    if (
      this.graphSetting.zAxis.value === 'engagementRate' ||
      this.graphSetting.zAxis.value === 'followersUpdownRate'
    ) {
      zAxisPercent = '%'
    }
    const self = this

    // グラフ設定をセット
    this.graphConfig = {
      chart: {
        type: 'bubble',
        plotBorderWidth: 1,
        zoomType: 'xy',
      },
      // Exportボタン
      navigation: {
        buttonOptions: {
          align: 'right',
        },
      },
      // Export設定
      exporting: {
        buttons: {
          contextButton: {
            menuItems: ['downloadPNG', 'downloadJPEG', 'downloadSVG'],
          },
        },
      },
      // 右下のクレジットをOFFにする
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      title: {
        text: '',
      },
      xAxis: {
        gridLineWidth: 1,
        title: {
          text: this.graphSetting.xAxis.text,
        },
        labels: {
          formatter: function () {
            return self.$filter('number')(this.value)
          },
        },
      },
      yAxis: {
        startOnTick: false,
        endOnTick: false,
        title: {
          text: this.graphSetting.yAxis.text,
        },
        labels: {
          formatter: function () {
            return self.$filter('number')(this.value)
          },
        },
        maxPadding: 0,
      },
      tooltip: {
        useHTML: true,
        pointFormatter: function () {
          let content = `
          <tr><th colspan="2"><h3>${this.fullName}</h3></th></tr>
          <tr><th>${self.graphSetting.yAxis.text}:</th>
          <td>${self.$filter('number')(this.y)}${yAxisPercent}</td>
          </tr>
          <tr><th>${self.graphSetting.xAxis.text}:</th>
          <td>${self.$filter('number')(this.x)}${xAxisPercent}</td>
          </tr>
          <tr><th>${self.graphSetting.zAxis.text}:</th>
          <td>${self.$filter('number')(this.z)}${zAxisPercent}</td>
          </tr>
          `
          // 大カテゴリまたは小カテゴリの場合は、アカウント数を表示する
          if (
            self.graphSetting.object === 'brandCategory' ||
            self.graphSetting.object === 'brandSubCategory'
          ) {
            content += `
            <tr><th>アカウント数:</th>
            <td>${self.$filter('number')(this.accounts)}</td>
            </tr>
            `
          }
          return content
        },
        headerFormat: '<table class="analytics-correaltion-tooltip" style="min-width: 300px">',
        footerFormat: '</table>',
        followPointer: true,
      },
      plotOptions: {
        series: {
          dataLabels: {
            enabled: true,
            format: '{point.name}',
          },
          cursor: 'pointer',
          point: {
            events: {
              click: function () {
                // 対象の投稿モダールを表示する
                self.showPostListModal(this.fullName, this.accountId)
              },
            },
          },
        },
      },
      series: [
        {
          data: this.graphData,
        },
      ],
    }
  }

  /**
   * グラフデータをセット
   * @private
   */
  private setGraphData() {
    this.graphData = []
    // 対象データをセット
    this.setObjectData()
    // 対象データをソート
    this.sortObjectData()
    // グラフデータシリーズをセット
    this.setGraphDataSeries()
  }

  /**
   * グラフデータシリーズをセット
   */
  private setGraphDataSeries() {
    this.objectDatas.forEach((account) => {
      const fullName = account.name
      let name = account.name
      if (name.length > 5) {
        name = account.name.substring(0, 5) + '...'
      }
      const data: any = {
        name: name,
        fullName: fullName,
        x: 0,
        y: 0,
        z: 0,
      }
      // 大カテゴリまたは小カテゴリの場合は、アカウント数をセット
      if (
        this.graphSetting.object === 'brandCategory' ||
        this.graphSetting.object === 'brandSubCategory'
      ) {
        data.accounts = account.accounts
      }
      // アカウントの場合は、アカウントIDをセット
      if (this.graphSetting.object === 'account') {
        data.accountId = account.accountId
      }
      // 軸キーをセット
      let xKey = this.graphSetting.xAxis.value
      let yKey = this.graphSetting.yAxis.value
      let zKey = this.graphSetting.zAxis.value
      // データ投稿あたりフラグがある場合は、セット
      if (this.graphSetting.dataPerPostFlg === true) {
        if (this.AnalyticsCorrelationService.dataPerPostExclude.indexOf(xKey) === -1) {
          xKey = xKey + 'PerPost'
        }
        if (this.AnalyticsCorrelationService.dataPerPostExclude.indexOf(yKey) === -1) {
          yKey = yKey + 'PerPost'
        }
        if (this.AnalyticsCorrelationService.dataPerPostExclude.indexOf(zKey) === -1) {
          zKey = zKey + 'PerPost'
        }
      }
      // X軸をセット
      if (account[xKey]) {
        data.x = account[xKey]
      }
      // Y軸をセット
      if (account[yKey]) {
        data.y = account[yKey]
      }
      // Z軸をセット
      if (account[zKey]) {
        data.z = account[zKey]
      }

      this.graphData.push(data)
    })
  }

  /**
   * 対象データをセット
   * @private
   */
  private setObjectData() {
    if (this.graphSetting.object === 'account') {
      this.objectDatas = this.accounts
      return
    }
    if (this.graphSetting.object === 'brandCategory') {
      // ブランドカテゴリデータをセット
      this.objectDatas = this.AnalyticsCorrelationService.setBrandCategoryData(this.accounts)
      return
    }
    if (this.graphSetting.object === 'brandSubCategory') {
      // ブランドサブカテゴリデータをセット
      this.objectDatas = this.AnalyticsCorrelationService.setBrandSubCategoryData(this.accounts)
      return
    }
  }

  /**
   * 対象データをソート
   * @private
   */
  private sortObjectData() {
    const viewCountType = this.graphSetting.viewCountType.split('-')
    const sortByType = viewCountType[0]
    const numberOf = parseInt(viewCountType[1], 10)
    let sortBy = ''
    switch (sortByType) {
      case 'xAxis':
        sortBy = this.graphSetting.xAxis.value
        break
      case 'yAxis':
        sortBy = this.graphSetting.yAxis.value
        break
      case 'zAxis':
        sortBy = this.graphSetting.zAxis.value
        break
    }
    // データ投稿あたりフラグがある場合は、セット
    if (
      this.graphSetting.dataPerPostFlg === true &&
      this.AnalyticsCorrelationService.dataPerPostExclude.indexOf(sortBy) === -1
    ) {
      sortBy = sortBy + 'PerPost'
    }
    // アカウント一覧データをソート
    this.objectDatas = this.AnalyticsCorrelationService.sortAccountData(
      this.objectDatas,
      sortBy,
      numberOf
    )
  }

  /**
   * 対象の投稿モダールを表示する
   * @private
   * @param {string} name アカウント名 | 大カテゴリ名 | 小カテゴリ名
   * @param {string} accountId アカウントID
   */
  private showPostListModal(name: string, accountId: string) {
    // モーダル用のスコープを新規に作成し、モーダル内で使用するパラメーターを格納
    const modalScope = this.$rootScope.$new()
    //  取得するオプション
    const getOptions: any = {
      userId: this.$rootScope.userId,
      groupId: this.$rootScope.groupId,
      startEndDate: this.filter.startEndDate,
      categoryFilter: this.filter.categoryName,
      subCategoryFilter: this.filter.subCategoryName,
      accountGlobalFilter: this.filter.accountGlobalFilter,
      followerFilter: this.filter.followerRangeFilter,
      accountTagFilter: this.filter.accountTagFilter,
      postTypeFilter: null,
      messageFilter: null,
      hashtagFilter: null,
    }
    // アカウントの場合
    if (this.graphSetting.object === 'account') {
      getOptions.accountId = accountId
    } else if (this.graphSetting.object === 'brandCategory') {
      // 大カテゴリの場合
      getOptions.categoryFilter = name
    } else if (this.graphSetting.object === 'brandSubCategory') {
      // 小カテゴリの場合
      getOptions.subCategoryFilter = name
    }
    // ターゲット
    modalScope.target = name
    // SNSタイプ
    modalScope.snsType = this.snsType
    modalScope.getOptions = getOptions

    // モーダルを表示する
    this.$mdDialog.show({
      template: require('../../post-list-modal/post-list-modal.pug')(),
      scope: modalScope,
      clickOutsideToClose: true,
    })
  }
}

app.component('analyticsCorrelationGraph', {
  bindings: {
    snsType: '<',
    accounts: '<',
    graphSetting: '<',
    filter: '<',
  },
  template: require('./analytics-correlation-graph.pug')(),
  controller: AnalyticsCorrelationGraphCtrl,
})
